import React from 'react';
import { Heading, Box, Flex, Switch, Label, Text, Button, Spinner } from '@indeed/ifl-components';
import { ArrowLeft, ArrowRight, MoreHorizontal } from '@indeed/ifl-icons';
import { SafeMarkup } from '@indeed/jspl-foundations';
import type { GetAutoApplyEligiblePastAnswersQuery } from './__generated__/PastAnswers.generated';
import { AnswerPreview, type AnswerPreviewType } from '../../../components/AnswerPreview';
import useGettext from '../../../util/GettextFactory';
import { jsProThemeButton } from '../components/jsProTheme/jsProTheme';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';

// Extract types from the generated GraphQL query (Relay edges/node pattern)
export type PastAnswerNode = NonNullable<
    GetAutoApplyEligiblePastAnswersQuery['autoApplyEligiblePastAnswers']
>['edges'][number]['node'];

/** Shared rendering shape adapted from either the SQ or DQ OneGraph response. */
export interface PastAnswerItem {
    question: {
        intentId: string;
        type: AnswerPreviewType;
        question: string;
        options: Array<{ value: string; label: string }>;
    };
    answers: Array<{
        values: string[];
        createdAt?: string | null;
    }>;
    domain?: string | null;
}

export interface PastAnswersContentProps {
    /** Array of past answers from GraphQL query (undefined = loading current page) */
    pastAnswers?: PastAnswerItem[];
    /** Whether to use past answers for pre-filling (undefined = consent not yet known). */
    usePastAnswers: boolean | undefined;
    /** Callback when toggle is changed */
    onUsePastAnswersChange: (value: boolean) => void;
    /** Label for the SQ or DQ autofill preference. */
    toggleLabel: string;
    /** Locale for date formatting (required) */
    locale: string;
}

/** Question types that have no user-provided answer and should not be displayed */
const HIDDEN_QUESTION_TYPES = new Set(['INFORMATION', 'PAGEBREAK']);

// Format date using locale-aware formatting
const formatDate = (dateString: string, locale: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
};

/**
 * Core content component for displaying past SQ or DQ answers.
 * Data-source agnostic - renders the questions list and toggle.
 *
 * Note: pastAnswers should contain only the items for the current page
 * (server-side pagination via GraphQL cursor-based pagination).
 * Pagination controls are handled separately via PaginationControls component.
 */
export const PastAnswersContent: React.FC<PastAnswersContentProps> = ({
    pastAnswers,
    usePastAnswers,
    onUsePastAnswersChange,
    toggleLabel,
    locale,
}) => {
    const gt = useGettext();
    const switchId = React.useId();
    const copy = useApplyForMeCopy();

    // Group questions by date (assuming answers are sorted by date descending (most recent first))
    // Filters out INFORMATION/PAGEBREAK types which have no user answer
    const groupedByDate = React.useMemo(() => {
        if (!pastAnswers) return [];

        const visible = pastAnswers.filter((pa) => !HIDDEN_QUESTION_TYPES.has(pa.question.type));

        const groups: [dateString: string | null, questions: PastAnswerItem[]][] = [];

        visible.forEach((pastAnswer) => {
            if (pastAnswer.answers.length === 0) return;

            const mostRecentAnswer = pastAnswer.answers[0];
            const answeredDate = mostRecentAnswer.createdAt;
            const formattedDate = answeredDate ? formatDate(answeredDate, locale) : null;

            if (groups[groups.length - 1]?.[0] !== formattedDate) {
                groups.push([formattedDate, []]);
            }
            groups[groups.length - 1][1].push(pastAnswer);
        });

        return groups;
    }, [pastAnswers, locale]);

    // Domain grouping spans date boundaries within the current page.
    let previousDomain: PastAnswerItem['domain'];

    return (
        <Flex sx={{ flexDirection: 'column' }}>
            {/* Toggle for opt-in/out with blue background */}
            <Flex
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBlock: 3,
                    paddingInline: 4,
                    backgroundColor: 'background.primary.weak',
                    marginInline: -4,
                }}
            >
                <Label htmlFor={usePastAnswers === undefined ? undefined : switchId}>
                    {toggleLabel}
                </Label>
                {usePastAnswers === undefined ? (
                    <Spinner title={gt.gettext('Loading')} />
                ) : (
                    <Switch
                        as="span"
                        id={switchId}
                        checked={usePastAnswers}
                        onChange={(e) => onUsePastAnswersChange(e.target.checked)}
                    />
                )}
            </Flex>

            {/* Past answers section */}
            <Box sx={{ paddingInline: 2, paddingBlockStart: 5 }}>
                <Heading level={5}>{gt.gettext('Past answers')}</Heading>

                {usePastAnswers === false || (pastAnswers && groupedByDate.length === 0) ? (
                    <Text level={2} sx={{ marginBlockStart: 2 }}>
                        {gt.gettext(
                            'Any new questions you answer will be added to this page. You can review them anytime.',
                        )}
                    </Text>
                ) : pastAnswers ? (
                    <>
                        <Text level={2} sx={{ marginBlockStart: 2 }}>
                            {copy.aiPrefillPastAnswersIntro}
                        </Text>
                        <Box sx={{ marginBlockStart: 4 }}>
                            {groupedByDate.map(([date, questions], groupIndex) => (
                                <Box key={`${date ?? 'unknown-date'}-${groupIndex}`}>
                                    {/* Date heading */}
                                    {date && (
                                        <Text level={1} sx={{ fontWeight: '700' }}>
                                            {date}
                                        </Text>
                                    )}

                                    {/* Questions for this date */}
                                    <Flex
                                        sx={{
                                            flexDirection: 'column',
                                            marginInlineStart: 4,
                                        }}
                                    >
                                        {questions.map((pastAnswer) => {
                                            const mostRecentAnswer = pastAnswer.answers[0];
                                            const showDomain =
                                                Boolean(pastAnswer.domain) &&
                                                pastAnswer.domain !== previousDomain;
                                            previousDomain = pastAnswer.domain;

                                            return (
                                                <Flex
                                                    key={`${pastAnswer.question.intentId}-${pastAnswer.question.question}-${pastAnswer.question.type}`}
                                                    sx={{
                                                        flexDirection: 'column',
                                                        paddingBlock: 3,
                                                        '&:not(:first-child)': {
                                                            borderBlockStart: '1px solid',
                                                            borderColor: 'border.weak',
                                                        },
                                                    }}
                                                >
                                                    {showDomain && (
                                                        <Text
                                                            level={1}
                                                            sx={{
                                                                fontWeight: '700',
                                                                marginBlockEnd: 1,
                                                            }}
                                                        >
                                                            {pastAnswer.domain}
                                                        </Text>
                                                    )}
                                                    <SafeMarkup
                                                        level={2}
                                                        sx={{
                                                            color: 'foreground.strong',
                                                        }}
                                                    >
                                                        {pastAnswer.question.question}
                                                    </SafeMarkup>
                                                    <AnswerPreview
                                                        type={pastAnswer.question.type}
                                                        values={mostRecentAnswer.values}
                                                        options={pastAnswer.question.options}
                                                        locale={locale}
                                                    />
                                                </Flex>
                                            );
                                        })}
                                    </Flex>
                                </Box>
                            ))}
                        </Box>
                    </>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 4,
                        }}
                    >
                        <Spinner title={gt.gettext('Loading')} />
                    </Box>
                )}
            </Box>
        </Flex>
    );
};

export interface PaginationControlsProps {
    /** Current page number (1-indexed) */
    currentPage: number;
    /** Total count of items across all pages */
    totalCount: number;
    /** Callback when page changes */
    onPageChange?: (page: number) => void;
    /** Items per page (default: 10) */
    itemsPerPage?: number;
    /** Disable all pagination controls (e.g. while fetching) */
    disabled?: boolean;
}

/**
 * Pagination controls component for past answers for screener questions
 * Can be used standalone or in DialogFooter
 * Always visible (even for single page) to maintain consistent layout
 */
export const PaginationControls: React.FC<PaginationControlsProps> = ({
    currentPage,
    totalCount,
    onPageChange,
    itemsPerPage = 10,
    disabled = false,
}) => {
    const gt = useGettext();

    const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

    const handlePageChange = (page: number) => {
        if (onPageChange && page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                gap: 2,
                '@media (max-width: 23.4375rem)': {
                    // 375px breakpoint for narrow viewports
                    gap: 0,
                },
            }}
        >
            {/* Previous button - fixed position in left column */}
            <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={disabled || currentPage === 1}
                iconOnly
                aria-label={gt.gettext('Previous page')}
                sx={{
                    minInlineSize: 'auto',
                    padding: 2,
                }}
            >
                <ArrowLeft aria-hidden />
            </Button>

            {/* Page numbers - centered in middle column */}
            <Flex
                sx={{
                    justifyContent: 'center',
                    gap: 1,
                    '@media (max-width: 23.4375rem)': {
                        // 375px breakpoint for narrow viewports
                        gap: 0,
                    },
                }}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                    // Show first page, last page, current page, and pages around current
                    const showPage =
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        Math.abs(pageNum - currentPage) <= 1;

                    // Show ellipsis
                    const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
                    const showEllipsisAfter =
                        pageNum === currentPage + 2 && currentPage < totalPages - 2;

                    if (showEllipsisBefore || showEllipsisAfter) {
                        return (
                            <Button
                                key={`ellipsis-${pageNum}`}
                                type="button"
                                variant="tertiary"
                                size="sm"
                                aria-label={gt.gettext('More pages')}
                                sx={{
                                    ...jsProThemeButton.tertiary,
                                    '@media (max-width: 23.4375rem)': {
                                        // 375px breakpoint for narrow viewports
                                        marginInline: -3,
                                    },
                                }}
                                iconOnly
                                disabled
                            >
                                <MoreHorizontal aria-hidden />
                            </Button>
                        );
                    }

                    if (!showPage) return null;

                    return (
                        <Button
                            key={pageNum}
                            type="button"
                            variant={pageNum === currentPage ? 'primary' : 'tertiary'}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={disabled}
                            aria-label={gt.gettext('Page {0}', [pageNum])}
                            aria-current={pageNum === currentPage ? 'page' : undefined}
                            sx={
                                pageNum === currentPage
                                    ? jsProThemeButton.primary
                                    : jsProThemeButton.tertiary
                            }
                        >
                            {pageNum}
                        </Button>
                    );
                })}
            </Flex>

            {/* Next button - fixed position in right column */}
            <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={disabled || currentPage >= totalPages}
                iconOnly
                aria-label={gt.gettext('Next page')}
                sx={{
                    minInlineSize: 'auto',
                    padding: 2,
                }}
            >
                <ArrowRight aria-hidden />
            </Button>
        </Box>
    );
};
